import { useState, useCallback } from 'react';
import {
  CRUDModalState,
  CRUDModalConfig,
  CRUDModalHook,
  CRUDModalOpenOptions
} from './types';
import { decodeFields } from '../../utils/htmlDecoder';

import { logger, getErrorMessage } from '../../utils/logger';
/**
 * Shared CRUD Modal Hook
 * 
 * Provides reusable logic for all entity-specific modal contexts:
 * - ID-based edit mode detection
 * - Fetch-on-demand for edit mode
 * - Loading state management
 * - Error handling
 * - Success/cancel callbacks
 * 
 * Industry Standard Pattern: Used by AdminJS, Retool, Strapi, Ant Design Pro
 * 
 * @template T - Entity type (Schedule, Webhook, User, etc.)
 * @param config - Modal configuration
 * @returns Modal state and actions
 * 
 * @example
 * ```typescript
 * // In ScheduleContext.tsx
 * const modal = useCRUDModal<Schedule>({
 *   entityName: 'schedule',
 *   fetchEndpoint: '/api/schedules'
 * });
 * 
 * // Use in provider
 * <CreateScheduleModal
 *   isOpen={modal.isOpen}
 *   mode={modal.mode}
 *   schedule={modal.record}
 *   isLoading={modal.isLoading}
 *   onClose={modal.close}
 *   onSaved={modal.handleSuccess}
 * />
 * ```
 * 
 * @example
 * ```typescript
 * // Usage in component
 * const { openScheduleModal } = useSchedule();
 * 
 * // Create mode
 * openScheduleModal();
 * 
 * // Edit mode
 * openScheduleModal({ recordId: 'abc123' });
 * ```
 */
export function useCRUDModal<T>(config: CRUDModalConfig<T>): CRUDModalHook<T> {
  // Initialize state with closed modal
  const [state, setState] = useState<CRUDModalState<T>>({
    isOpen: false,
    mode: 'create',
    recordId: null,
    record: null,
    isLoading: false,
    error: null
  });

  // Store callbacks in state to access in close/handleSuccess
  const [callbacks, setCallbacks] = useState<{
    onSuccess?: () => void;
    onCancel?: () => void;
  }>({});

  /**
   * Fetch record from API
   * Uses custom fetchFn if provided, otherwise uses default fetch
   * 
   * @param id - Record ID to fetch
   */
  const fetchRecord = useCallback(async (id: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      let data: T;

      if (config.fetchFn) {
        // Use custom fetch function if provided
        data = await config.fetchFn(id);
      } else {
        // Default fetch implementation
        const token = config.authToken || localStorage.getItem('token');
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${config.fetchEndpoint}/${id}`, {
          headers
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(`${config.entityName} not found`);
          }
          throw new Error(`Failed to fetch ${config.entityName}`);
        }

        const responseData = await response.json();
        
        // ✅ CRITICAL FIX: Backend returns { success: true, data: {...} }
        // Extract the actual record from the wrapper
        if (responseData.success && responseData.data) {
          data = responseData.data;
        } else {
          // Fallback: if API returns record directly (for backward compatibility)
          data = responseData;
        }
        
        // ✅ DECODE HTML ENTITIES: API may send encoded fields for JSON safety
        // Decode common fields that typically contain special characters
        if (config.entityName === 'schedule') {
          data = decodeFields(data as Record<string, any>, [
            'timezone',       // Asia&#x2F;Kolkata → Asia/Kolkata
            'cronSchedule',   // *&#x2F;15 → */15
            'name',           // Test &amp; Demo → Test & Demo
            'description'     // Any description with special chars
          ]) as T;
        }
      }

      setState(prev => ({
        ...prev,
        record: data,
        isLoading: false,
        error: null
      }));

      logger.debug(`[CRUD-MODAL] Fetched ${config.entityName}:`, { data: id });
    } catch (error: any) {
      logger.error(`[CRUD-MODAL] Fetch failed for ${config.entityName}:`, { error: getErrorMessage(error) });
      
      setState(prev => ({
        ...prev,
        record: null,
        isLoading: false,
        error: error.message
      }));
    }
  }, [config]);

  /**
   * Open modal in create or edit mode
   * 
   * @param options - Open options (recordId, callbacks)
   */
  const open = useCallback((options: CRUDModalOpenOptions = {}) => {
    const { recordId, onSuccess, onCancel } = options;
    const mode = recordId ? 'edit' : 'create';

    logger.debug(`[CRUD-MODAL] Opening ${config.entityName} modal:`, {
      mode,
      recordId: recordId || 'none (create mode)'
    });

    // Store callbacks
    setCallbacks({ onSuccess, onCancel });

    // Update state
    setState({
      isOpen: true,
      mode,
      recordId: recordId || null,
      record: null,
      isLoading: !!recordId, // Loading only if edit mode
      error: null
    });

    // Fetch record if edit mode
    if (recordId) {
      fetchRecord(recordId);
    }
  }, [config.entityName, fetchRecord]);

  /**
   * Close modal
   * Triggers onCancel callback if provided
   * Resets all state after animation completes
   */
  const close = useCallback(() => {
    logger.debug(`[CRUD-MODAL] Closing ${config.entityName} modal`);

    // Trigger cancel callback if provided
    if (callbacks.onCancel) {
      callbacks.onCancel();
    }

    // Close modal immediately
    setState(prev => ({ ...prev, isOpen: false }));

    // Clear state after animation completes (300ms)
    setTimeout(() => {
      setState({
        isOpen: false,
        mode: 'create',
        recordId: null,
        record: null,
        isLoading: false,
        error: null
      });
      setCallbacks({});
    }, 300);
  }, [config.entityName, callbacks]);

  /**
   * Handle successful save
   * Triggers onSuccess callback and closes modal
   */
  const handleSuccess = useCallback(() => {
    logger.debug(`[CRUD-MODAL] ${config.entityName} saved successfully`);

    // Trigger success callback if provided
    if (callbacks.onSuccess) {
      callbacks.onSuccess();
    }

    // Close modal
    close();
  }, [config.entityName, callbacks, close]);

  /**
   * Refresh current record (for edit mode)
   * Useful for refetching data after external changes
   */
  const refresh = useCallback(() => {
    if (state.recordId) {
      logger.debug(`[CRUD-MODAL] Refreshing ${config.entityName}:`, { data: state.recordId });
      fetchRecord(state.recordId);
    } else {
      logger.warn('[CRUD-MODAL] Cannot refresh: No recordId (not in edit mode)');
    }
  }, [config.entityName, state.recordId, fetchRecord]);

  return {
    // State
    isOpen: state.isOpen,
    mode: state.mode,
    recordId: state.recordId,
    record: state.record,
    isLoading: state.isLoading,
    error: state.error,

    // Actions
    open,
    close,
    handleSuccess,
    refresh
  };
}

